/**
 * Collaborative Analytics Canvas - Looker Studio Community Visualization
 * Powered by Cap'n Web WebSocket RPC
 *
 * Features:
 * - Real-time collaborative annotations
 * - AI-powered forecasting overlays
 * - Live SQL query execution
 * - Multi-user cursors and presence
 * - Anomaly detection and highlighting
 */

// NOTE: In production, Cap'n Web would be bundled or loaded via CDN
// For this example, we'll inline the core RPC logic

// ============================================================================
// CAP'N WEB RPC CLIENT (Simplified for Looker Studio)
// ============================================================================

class WebSocketRpcSession {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this.pendingCalls = new Map();
    this.callId = 0;
    this.callbacks = new Map();
    this.connected = false;
  }

  async connect() {
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        this.connected = true;
        console.log('[Cap\'n Web] WebSocket connected');
        resolve();
      };

      this.ws.onerror = (error) => {
        console.error('[Cap\'n Web] WebSocket error:', error);
        reject(error);
      };

      this.ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      };

      this.ws.onclose = () => {
        this.connected = false;
        console.log('[Cap\'n Web] WebSocket closed');
        this.attemptReconnect();
      };
    });
  }

  handleMessage(message) {
    console.log('[RPC Response] Received:', message);

    if (message.type === 'response') {
      const pending = this.pendingCalls.get(message.id);
      if (pending) {
        if (message.error) {
          pending.reject(new Error(message.error));
        } else {
          pending.resolve(message.result);
        }
        this.pendingCalls.delete(message.id);
      }
    } else if (message.type === 'callback') {
      console.log('[RPC Callback] Received:', message.callbackId, message.data);
      const callback = this.callbacks.get(message.callbackId);
      if (callback) {
        callback(message.data);
      } else {
        console.warn('[RPC Callback] No handler for:', message.callbackId);
      }
    }
  }

  call(method, ...args) {
    return new Promise((resolve, reject) => {
      const id = this.callId++;
      this.pendingCalls.set(id, { resolve, reject });

      const message = {
        type: 'call',
        id,
        method,
        args
      };

      console.log('[RPC Call] Sending:', method, 'with args:', args);
      this.ws.send(JSON.stringify(message));
    });
  }

  registerCallback(callbackId, callback) {
    this.callbacks.set(callbackId, callback);
  }

  attemptReconnect() {
    setTimeout(() => {
      if (!this.connected) {
        this.connect().catch(() => this.attemptReconnect());
      }
    }, 3000);
  }

  dispose() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

// ============================================================================
// LOOKER STUDIO VISUALIZATION
// ============================================================================

let rpcSession = null;
let annotationChannel = null;
let presenceChannel = null;
let currentUser = null;
let annotations = [];
let activeCursors = new Map();

// Initialize the visualization
function drawViz(data) {
  const container = document.getElementById('container');
  container.innerHTML = ''; // Clear previous content

  // Get configuration
  const config = data.style;
  const chartData = data.tables.timeseries;

  // Initialize RPC connection if not already connected
  initializeRpc(data).then(() => {
    // Render the main chart
    renderChart(container, chartData, config);

    // Add collaborative features
    if (config.enable_annotations.value) {
      setupAnnotations(container);
    }

    if (config.enable_forecasting.value) {
      addForecastOverlay(container, chartData, config);
    }

    if (config.enable_live_cursors.value) {
      setupLiveCursors(container);
    }

    if (config.enable_sql_editor.value) {
      addSqlEditor(container, config);
    }
  }).catch(error => {
    showError(container, 'Failed to connect to RPC server: ' + error.message);
  });
}

async function initializeRpc(data) {
  if (rpcSession && rpcSession.connected) {
    return; // Already connected
  }

  const websocketUrl = data.style.websocket_url.value;
  const userEmail = data.metadata.EMAIL || 'anonymous@example.com';
  const reportId = data.metadata.REPORT_ID || 'unknown-report';

  // Add roomId query parameter to WebSocket URL
  const wsUrl = new URL(websocketUrl);
  wsUrl.searchParams.set('roomId', reportId);

  rpcSession = new WebSocketRpcSession(wsUrl.toString());
  await rpcSession.connect();

  // Authenticate user
  currentUser = await rpcSession.call('authenticate', userEmail);

  // Join collaboration channels
  annotationChannel = await rpcSession.call('joinAnnotationRoom', reportId);
  presenceChannel = await rpcSession.call('joinPresenceRoom', reportId);

  // Register callbacks for real-time updates
  rpcSession.registerCallback('newAnnotation', (annotation) => {
    annotations.push(annotation);
    rerenderAnnotations();
  });

  rpcSession.registerCallback('cursorMove', (cursor) => {
    activeCursors.set(cursor.userId, cursor);
    rerenderCursors();
  });

  rpcSession.registerCallback('userJoined', (user) => {
    showNotification(`${user.name} joined the report`);
  });

  console.log('[Collaborative Canvas] RPC initialized for user:', currentUser.name);
}

function renderChart(container, chartData, config) {
  const chartContainer = document.createElement('div');
  chartContainer.className = 'chart-container';
  chartContainer.id = 'chart';

  // Create SVG for the chart with viewBox for proper scaling
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '400');
  svg.setAttribute('viewBox', '0 0 800 400');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.style.backgroundColor = '#0f172a';

  // Parse and render data
  const rows = chartData.rows || [];
  const timestamp = chartData.headers[0];
  const metrics = chartData.headers.slice(1);

  // Draw grid if enabled
  if (config.show_grid.value) {
    drawGrid(svg);
  }

  // Draw data series
  metrics.forEach((metric, index) => {
    const color = getColor(index, config.color_scheme.value);
    drawSeries(svg, rows, index + 1, color, config.chart_type.value);
  });

  chartContainer.appendChild(svg);
  container.appendChild(chartContainer);
}

function drawGrid(svg) {
  const gridLines = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  gridLines.setAttribute('class', 'grid');

  // Horizontal lines
  for (let i = 0; i <= 10; i++) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    const y = (i / 10) * 400;
    line.setAttribute('x1', '0');
    line.setAttribute('y1', y);
    line.setAttribute('x2', '800');
    line.setAttribute('y2', y);
    line.setAttribute('stroke', '#334155');
    line.setAttribute('stroke-width', '1');
    gridLines.appendChild(line);
  }

  svg.appendChild(gridLines);
}

function drawSeries(svg, rows, metricIndex, color, chartType) {
  if (!rows || rows.length === 0) return;

  // Calculate min/max for better scaling
  const values = rows.map(row => parseFloat(row[metricIndex] || 0));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueRange = maxValue - minValue || 1;

  const points = rows.map((row, index) => {
    const x = (index / Math.max(rows.length - 1, 1)) * 800; // Use viewBox width
    const value = parseFloat(row[metricIndex] || 0);
    // Map value to SVG coordinate space (with padding)
    const normalizedValue = (value - minValue) / valueRange;
    const y = 350 - (normalizedValue * 300); // Leave 50px padding top/bottom
    return { x, y, value };
  });

  if (chartType === 'line' || chartType === 'area') {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const pathData = points.map((p, i) =>
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    path.setAttribute('d', pathData);
    path.setAttribute('stroke', color);
    path.setAttribute('stroke-width', '3');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');

    if (chartType === 'area') {
      // Create area fill
      const areaPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const areaData = pathData + ` L ${points[points.length - 1].x} 400 L 0 400 Z`;
      areaPath.setAttribute('d', areaData);
      areaPath.setAttribute('fill', color);
      areaPath.setAttribute('opacity', '0.2');
      svg.appendChild(areaPath);
    }

    svg.appendChild(path);
  } else if (chartType === 'scatter') {
    points.forEach(p => {
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', p.x);
      circle.setAttribute('cy', p.y);
      circle.setAttribute('r', '5');
      circle.setAttribute('fill', color);
      circle.setAttribute('opacity', '0.8');
      svg.appendChild(circle);
    });
  } else if (chartType === 'bar') {
    const barWidth = (800 / rows.length) * 0.8;
    points.forEach((p, index) => {
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', p.x - barWidth / 2);
      rect.setAttribute('y', p.y);
      rect.setAttribute('width', barWidth);
      rect.setAttribute('height', 400 - p.y);
      rect.setAttribute('fill', color);
      rect.setAttribute('opacity', '0.8');
      svg.appendChild(rect);
    });
  }
}

function setupAnnotations(container) {
  const annotationContainer = document.createElement('div');
  annotationContainer.className = 'annotation-container';
  annotationContainer.id = 'annotations';

  // Add annotation button
  const addButton = document.createElement('button');
  addButton.className = 'add-annotation-btn';
  addButton.textContent = '+ Add Annotation';
  addButton.onclick = () => createAnnotation();

  annotationContainer.appendChild(addButton);
  container.appendChild(annotationContainer);

  // Load existing annotations
  loadAnnotations();
}

async function createAnnotation() {
  const text = prompt('Enter annotation:');
  if (!text) return;

  console.log('[Create Annotation] User input:', text);
  console.log('[Create Annotation] Current user:', currentUser);
  console.log('[Create Annotation] Annotation channel:', annotationChannel);
  console.log('[Create Annotation] RPC session:', rpcSession);

  const annotation = {
    id: Date.now().toString(),
    userId: currentUser.id,
    userName: currentUser.name,
    text,
    timestamp: new Date().toISOString(),
    x: Math.random() * 80 + 10, // Random position
    y: Math.random() * 300 + 50
  };

  console.log('[Create Annotation] Sending via RPC:', annotation);

  try {
    // Send via RPC
    const result = await rpcSession.call('addAnnotation', annotationChannel, annotation);
    console.log('[Create Annotation] RPC response:', result);

    annotations.push(annotation);
    rerenderAnnotations();
    console.log('[Create Annotation] Annotation added successfully');
  } catch (error) {
    console.error('[Create Annotation] Error:', error);
  }
}

function rerenderAnnotations() {
  const svg = document.getElementById('chart').querySelector('svg');
  const existingAnnotations = svg.querySelectorAll('.annotation');
  existingAnnotations.forEach(el => el.remove());

  annotations.forEach(annotation => {
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('class', 'annotation');
    group.setAttribute('transform', `translate(${annotation.x}, ${annotation.y})`);

    // Pin marker
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('r', '6');
    circle.setAttribute('fill', '#3b82f6');
    circle.setAttribute('stroke', '#fff');
    circle.setAttribute('stroke-width', '2');

    // Tooltip
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', '10');
    text.setAttribute('y', '5');
    text.setAttribute('fill', '#e2e8f0');
    text.setAttribute('font-size', '12');
    text.textContent = `${annotation.userName}: ${annotation.text}`;

    group.appendChild(circle);
    group.appendChild(text);
    svg.appendChild(group);
  });
}

async function loadAnnotations() {
  annotations = await rpcSession.call('getAnnotations', annotationChannel);
  rerenderAnnotations();
}

async function addForecastOverlay(container, chartData, config) {
  const forecastContainer = document.createElement('div');
  forecastContainer.className = 'forecast-container';
  forecastContainer.innerHTML = '<div class="loading">Generating AI forecast...</div>';
  container.appendChild(forecastContainer);

  try {
    // Request forecast from AI service via RPC
    const periods = parseInt(config.forecast_periods.value);
    const model = config.forecast_model.value;
    const confidence = parseFloat(config.confidence_interval.value) / 100;

    const forecast = await rpcSession.call('generateForecast', {
      data: chartData.rows,
      periods,
      model,
      confidence
    });

    // Render forecast overlay
    renderForecastOverlay(forecast, config);
    forecastContainer.querySelector('.loading').remove();

  } catch (error) {
    forecastContainer.innerHTML = `<div class="error">Forecast failed: ${error.message}</div>`;
  }
}

function renderForecastOverlay(forecast, config) {
  const svg = document.getElementById('chart').querySelector('svg');
  const forecastGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  forecastGroup.setAttribute('class', 'forecast-overlay');

  // Draw forecast line
  forecast.predictions.forEach((point, index) => {
    const x = (point.timestamp / forecast.maxTimestamp) * 100;
    const y = 400 - (point.value * 3.5);

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', `${x}%`);
    circle.setAttribute('cy', y);
    circle.setAttribute('r', '3');
    circle.setAttribute('fill', '#f59e0b');
    circle.setAttribute('opacity', '0.7');
    forecastGroup.appendChild(circle);

    // Confidence interval
    if (point.upper && point.lower) {
      const upperY = 400 - (point.upper * 3.5);
      const lowerY = 400 - (point.lower * 3.5);

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', `${x}%`);
      rect.setAttribute('y', upperY);
      rect.setAttribute('width', '2');
      rect.setAttribute('height', lowerY - upperY);
      rect.setAttribute('fill', '#f59e0b');
      rect.setAttribute('opacity', '0.2');
      forecastGroup.appendChild(rect);
    }
  });

  // Highlight anomalies if enabled
  if (config.show_anomalies.value && forecast.anomalies) {
    forecast.anomalies.forEach(anomaly => {
      const x = (anomaly.timestamp / forecast.maxTimestamp) * 100;
      const y = 400 - (anomaly.value * 3.5);

      const marker = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      marker.setAttribute('cx', `${x}%`);
      marker.setAttribute('cy', y);
      marker.setAttribute('r', '8');
      marker.setAttribute('fill', 'none');
      marker.setAttribute('stroke', '#ef4444');
      marker.setAttribute('stroke-width', '2');
      forecastGroup.appendChild(marker);
    });
  }

  svg.appendChild(forecastGroup);
}

function setupLiveCursors(container) {
  const cursorContainer = document.createElement('div');
  cursorContainer.className = 'cursor-container';
  cursorContainer.id = 'cursors';
  container.appendChild(cursorContainer);

  // Track local cursor movement
  container.addEventListener('mousemove', (e) => {
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Send cursor position via RPC
    rpcSession.call('updateCursor', presenceChannel, {
      userId: currentUser.id,
      userName: currentUser.name,
      x,
      y
    });
  });
}

function rerenderCursors() {
  const cursorContainer = document.getElementById('cursors');
  cursorContainer.innerHTML = '';

  activeCursors.forEach((cursor, userId) => {
    if (userId === currentUser.id) return; // Don't show own cursor

    const cursorEl = document.createElement('div');
    cursorEl.className = 'remote-cursor';
    cursorEl.style.left = `${cursor.x}%`;
    cursorEl.style.top = `${cursor.y}%`;
    cursorEl.innerHTML = `
      <div class="cursor-dot"></div>
      <div class="cursor-label">${cursor.userName}</div>
    `;
    cursorContainer.appendChild(cursorEl);
  });
}

function addSqlEditor(container, config) {
  const editorContainer = document.createElement('div');
  editorContainer.className = 'sql-editor-container';
  editorContainer.innerHTML = `
    <h3>Live SQL Query</h3>
    <textarea id="sql-query" placeholder="SELECT * FROM data WHERE..."></textarea>
    <button onclick="executeSqlQuery()">Execute Query</button>
    <div id="query-results"></div>
  `;
  container.appendChild(editorContainer);
}

async function executeSqlQuery() {
  const query = document.getElementById('sql-query').value;
  const resultsContainer = document.getElementById('query-results');

  resultsContainer.innerHTML = '<div class="loading">Executing query...</div>';

  try {
    const results = await rpcSession.call('executeQuery', query);
    displayQueryResults(resultsContainer, results);
  } catch (error) {
    resultsContainer.innerHTML = `<div class="error">Query failed: ${error.message}</div>`;
  }
}

function displayQueryResults(container, results) {
  if (!results || !results.rows || results.rows.length === 0) {
    container.innerHTML = '<div>No results</div>';
    return;
  }

  let html = '<table class="query-results-table"><thead><tr>';
  results.columns.forEach(col => {
    html += `<th>${col}</th>`;
  });
  html += '</tr></thead><tbody>';

  results.rows.forEach(row => {
    html += '<tr>';
    row.forEach(cell => {
      html += `<td>${cell}</td>`;
    });
    html += '</tr>';
  });
  html += '</tbody></table>';

  container.innerHTML = html;
}

// Helper functions
function getColor(index, scheme) {
  const schemes = {
    default: ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'],
    vibrant: ['#ff006e', '#00f5ff', '#ffbe0b', '#8338ec', '#06ffa5'],
    mono: ['#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0', '#f1f5f9'],
    pastel: ['#ffd6ff', '#e7c6ff', '#c8b6ff', '#b8c0ff', '#bbd0ff']
  };
  return schemes[scheme][index % schemes[scheme].length];
}

function showError(container, message) {
  container.innerHTML = `<div class="error-message">${message}</div>`;
}

function showNotification(message) {
  console.log('[Notification]', message);
  // In production, show a toast notification
}

// Cleanup on visualization destroy
function cleanupViz() {
  if (rpcSession) {
    rpcSession.dispose();
    rpcSession = null;
  }
}

// Export drawViz globally for dev mode
window.drawViz = drawViz;

// Looker Studio API integration
dscc.subscribeToData(drawViz, { transform: dscc.objectTransform });
